import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { QueuedCall } from '../hooks/usePhoneCalls';

/**
 * CallDecisionModal — Birleşik Kuyruk Sistemi zorunlu karar modalı.
 *
 * İki mod:
 *  - 'new-call'      : Kasiyer bir müşteriyle meşgulken YENİ arama geldi.
 *                      Seçenekler: "Aramayı Beklet — Siparişi Tamamla" / "Yeni Müşteriye Geç"
 *  - 'next-in-queue' : Ekran serbest kaldı ve sıra kuyrukta bekleyen aramaya geldi.
 *                      Seçenekler: "Müşteriye Geç" (birincil) / "Beklemeye Devam Et" (60 sn snooze)
 *
 * Zorunlu karar: backdrop tıklaması ve ESC ile KAPANMAZ (onClose verilmez,
 * disableEscapeKeyDown). zIndex 1600 — QuickSale (1300) ve bildirim
 * stack'inin (1400) üstünde görünür.
 */

export type CallDecisionMode = 'new-call' | 'next-in-queue';

export interface CallDecisionModalProps {
  open: boolean;
  call: QueuedCall | null;
  mode: CallDecisionMode;
  /** Kuyruktaki toplam bekleyen arama sayısı (bilgi amaçlı) */
  pendingCount: number;
  themeColor: string;
  /** Aramayı beklet / beklemeye devam et — arama kuyrukta held olarak kalır */
  onHold: () => void;
  /** Yeni müşteriye geç — mevcut bağlam kapatılır, bekleyen müşteri yüklenir */
  onSwitch: () => void;
  /** Sipariş penceresi açık mı? Açıksa geçiş ikinci bir onay ister (yarım sepet kapanır). */
  orderInProgress?: boolean;
}

/** Pencere açıldıktan sonra düğmeler bu süre boyunca dokunmaya kapalıdır. */
const ARM_DELAY_MS = 1500;

const CallDecisionModal: React.FC<CallDecisionModalProps> = ({
  open,
  call,
  mode,
  pendingCount,
  themeColor,
  onHold,
  onSwitch,
  orderInProgress = false,
}) => {
  const isNextInQueue = mode === 'next-in-queue';

  // Pencere kasiyer ürünlere dokunurken ekranın ortasında açılıyor; ilk dokunuş
  // yanlışlıkla "Yeni Müşteriye Geç"e denk gelip yarım siparişi kapatmasın.
  const [armed, setArmed] = React.useState(false);
  const [confirmSwitch, setConfirmSwitch] = React.useState(false);
  const callKey = call ? `${call.call.phone}-${call.startedAt}` : '';
  React.useEffect(() => {
    setArmed(false);
    setConfirmSwitch(false);
    if (!open) return;
    const t = setTimeout(() => setArmed(true), ARM_DELAY_MS);
    return () => clearTimeout(t);
  }, [open, callKey]);

  const handleSwitchClick = () => {
    if (!armed) return;
    if (orderInProgress && !confirmSwitch) {
      setConfirmSwitch(true);
      return;
    }
    onSwitch();
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      sx={{ zIndex: 1600 }}
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      {/* Başlık bandı */}
      <Box
        sx={{
          bgcolor: isNextInQueue ? '#FF9800' : '#d32f2f',
          color: 'white',
          p: 2.5,
          textAlign: 'center',
          animation: 'callDecisionPulse 1.5s infinite',
          '@keyframes callDecisionPulse': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.85 },
            '100%': { opacity: 1 },
          },
        }}
      >
        <PhoneIcon sx={{ fontSize: 44, mb: 0.5 }} />
        <Typography variant="h5" fontWeight="bold">
          {isNextInQueue ? 'SIRADAKİ MÜŞTERİ' : 'YENİ GELEN ARAMA'}
        </Typography>
      </Box>

      <DialogContent sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 1.5, color: themeColor }}>
          {call?.call.phone || 'Bilinmeyen Numara'}
        </Typography>

        {call?.call.customerName ? (
          <Chip
            icon={<PersonIcon />}
            label={call.call.customerName}
            color="primary"
            sx={{ fontSize: '1.05rem', py: 2.25, px: 1.5, mb: 1.5 }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Kayıtlı olmayan numara
          </Typography>
        )}

        {call?.ringEnded && (
          <Typography variant="body2" color="warning.main" fontWeight="bold" sx={{ mb: 1 }}>
            Zil sonlandı — müşteri sırada bekliyor
          </Typography>
        )}

        <Typography variant="body1" sx={{ mt: 1 }}>
          {isNextInQueue
            ? 'Ekran serbest kaldı. Sırada bekleyen müşteriye geçmek ister misiniz?'
            : 'Şu anda başka bir müşteriyle işlem yapıyorsunuz. Yeni arayan müşteri için ne yapmak istersiniz?'}
        </Typography>

        {confirmSwitch && (
          <Typography variant="body1" color="error" fontWeight="bold" sx={{ mt: 1.5 }}>
            Girilmekte olan sipariş kapatılacak ve sepetteki ürünler silinecek. Emin misiniz?
          </Typography>
        )}

        {pendingCount > 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Kuyrukta toplam {pendingCount} bekleyen arama var
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3, flexWrap: 'wrap' }}>
        <Button
          variant={isNextInQueue ? 'outlined' : 'contained'}
          color="warning"
          size="large"
          startIcon={<HourglassBottomIcon />}
          onClick={() => { if (armed) onHold(); }}
          disabled={!armed}
          sx={{ fontWeight: 'bold', px: 3, py: 1.25 }}
        >
          {isNextInQueue ? 'Beklemeye Devam Et' : 'Aramayı Beklet — Siparişi Tamamla'}
        </Button>
        <Button
          variant="contained"
          color={confirmSwitch ? 'error' : 'success'}
          size="large"
          startIcon={<SwapHorizIcon />}
          onClick={handleSwitchClick}
          disabled={!armed}
          sx={{ fontWeight: 'bold', px: 3, py: 1.25 }}
        >
          {confirmSwitch ? 'Evet, Siparişi Bırak ve Geç' : isNextInQueue ? 'Müşteriye Geç' : 'Yeni Müşteriye Geç'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CallDecisionModal;
